import React, { CSSProperties } from 'react';
import { CustomColumn } from '../../../../types/components/tables/tableTypes';
import { greys } from '../../../../styling/theme';
import {
  addCommasToNumbers,
  percentageToNdecialPlacesNoHundred,
  roundNumberToNDecimalPlaces,
} from '../../../../utilities/numberFormatters';
import GenericStatusCell from '../../../tables/GenericStatusCell';
import { Status } from '../../../../types/redux/data/dataTypes';
import { NavReconShareClass } from '../../../../api/rs-hg/nav-recon/types';

export type NavReconShareClassTableRow = NavReconShareClass & {
  index: number;
  nav_t_minus_1: number;
  nav_t: number;
  reds: number;
  subs: number;
  nav_fx_t_minus_1: number;
  nav_fx_t: number;
  dividend: number;
  price_fx_t_minus_1: number;
  price_fx_t: number;
  price_t_minus_1: number;
  price_t: number;
};

const cellBase: CSSProperties = { fontSize: '0.8rem', color: greys.grey900 };
const numericCell: CSSProperties = {
  ...cellBase,
  textAlign: 'center',
  whiteSpace: 'nowrap',
  minWidth: '5rem',
};
const centeredHeader: CSSProperties = {
  whiteSpace: 'nowrap',
  textAlign: 'center',
  minWidth: '5rem',
};

const formatUnits = (v: number | undefined) => {
  if (v === undefined || Number.isNaN(v)) return '-';
  return addCommasToNumbers(roundNumberToNDecimalPlaces(v, 4));
};

export const getNavReconShareClassesColumns =
  (): CustomColumn<NavReconShareClassTableRow>[] => [
    {
      title: '#',
      field: 'index',
      export: false,
      cellStyle: { ...cellBase, textAlign: 'center', width: '2.5rem' },
      headerStyle: { ...centeredHeader, width: '2.5rem' },
    },
    {
      title: 'Share class',
      field: 'share_class',
      cellStyle: { ...cellBase, fontWeight: 600 },
      headerStyle: centeredHeader,
    },
    {
      title: 'Hedged',
      field: 'is_share_class_hedged',
      cellStyle: { ...cellBase, textAlign: 'center' },
      headerStyle: centeredHeader,
      render: (row) => {
        if (row.is_share_class_hedged == null) return '-';
        return row.is_share_class_hedged ? 'Yes' : 'No';
      },
    },
    {
      title: 'Reconciled',
      field: 'is_reconciled',
      cellStyle: { ...cellBase, textAlign: 'center' },
      headerStyle: centeredHeader,
      render: (row) => (
        <GenericStatusCell
          status={row.is_reconciled ? Status.Pass : Status.Fail}
          innerText={row.is_reconciled ? 'Yes' : 'No'}
          height="2rem"
          fontSize="0.75rem"
        />
      ),
    },
    {
      title: 'Price return',
      field: 'price_return',
      cellStyle: numericCell,
      headerStyle: centeredHeader,
      render: (row) => percentageToNdecialPlacesNoHundred(row.price_return, 4),
    },
    {
      title: 'NAV return',
      field: 'nav_return',
      cellStyle: numericCell,
      headerStyle: centeredHeader,
      render: (row) => percentageToNdecialPlacesNoHundred(row.nav_return, 4),
    },
    {
      title: 'Units change',
      field: 'units_change',
      cellStyle: numericCell,
      headerStyle: centeredHeader,
      render: (row) => formatUnits(row.units_change),
    },
    {
      title: 'Units T−1',
      field: 'units_t_minus_1',
      cellStyle: numericCell,
      headerStyle: centeredHeader,
      render: (row) => formatUnits(row.units_t_minus_1),
    },
    {
      title: 'Units at T',
      field: 'units_at_t',
      cellStyle: numericCell,
      headerStyle: centeredHeader,
      render: (row) => formatUnits(row.units_at_t),
    },
    {
      title: 'NAV T−1',
      field: 'nav_t_minus_1',
      cellStyle: numericCell,
      headerStyle: centeredHeader,
      render: (row) => formatUnits(row.nav_t_minus_1),
    },
    {
      title: 'NAV T',
      field: 'nav_t',
      cellStyle: numericCell,
      headerStyle: centeredHeader,
      render: (row) => formatUnits(row.nav_t),
    },
    {
      title: 'Reds',
      field: 'reds',
      cellStyle: numericCell,
      headerStyle: centeredHeader,
      render: (row) => formatUnits(row.reds),
    },
    {
      title: 'Subs',
      field: 'subs',
      cellStyle: numericCell,
      headerStyle: centeredHeader,
      render: (row) => formatUnits(row.subs),
    },
    {
      title: 'NAV FX T−1',
      field: 'nav_fx_t_minus_1',
      cellStyle: numericCell,
      headerStyle: centeredHeader,
      render: (row) => formatUnits(row.nav_fx_t_minus_1),
    },
    {
      title: 'NAV FX T',
      field: 'nav_fx_t',
      cellStyle: numericCell,
      headerStyle: centeredHeader,
      render: (row) => formatUnits(row.nav_fx_t),
    },
    {
      title: 'Dividend',
      field: 'dividend',
      cellStyle: numericCell,
      headerStyle: centeredHeader,
      render: (row) => formatUnits(row.dividend),
    },
    {
      title: 'Price T−1',
      field: 'price_t_minus_1',
      cellStyle: numericCell,
      headerStyle: centeredHeader,
      render: (row) => formatUnits(row.price_t_minus_1),
    },
    {
      title: 'Price T',
      field: 'price_t',
      cellStyle: numericCell,
      headerStyle: centeredHeader,
      render: (row) => formatUnits(row.price_t),
    },
    {
      title: 'Price FX T−1',
      field: 'price_fx_t_minus_1',
      cellStyle: numericCell,
      headerStyle: centeredHeader,
      render: (row) => formatUnits(row.price_fx_t_minus_1),
    },
    {
      title: 'Price FX T',
      field: 'price_fx_t',
      cellStyle: numericCell,
      headerStyle: centeredHeader,
      render: (row) => formatUnits(row.price_fx_t),
    },
  ];
